import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft,
    Loader2,
    MessageCircle,
    TrendingUp,
    MousePointer,
    Hash,
    BarChart3,
    Eye,
    X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatAnalytics() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [viewing, setViewing] = useState(null); // conversation_id | null

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner") {
            toast.error("Admin only");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    useEffect(() => {
        if (user?.role !== "owner") return;
        (async () => {
            setBusy(true);
            try {
                const r = await axios.get(`${API}/coach/chat/analytics`, { withCredentials: true });
                setData(r.data);
            } catch (e) {
                toast.error("Couldn't load analytics", { description: e?.response?.data?.detail });
            } finally {
                setBusy(false);
            }
        })();
    }, [user]);

    if (loading || busy || !data) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
    }
    if (user?.role !== "owner") return null;

    const t = data.totals;
    const c = data.conversion;

    return (
        <div data-testid="chat-analytics" className="min-h-screen bg-black text-white">
            <div className="max-w-6xl mx-auto px-4 pt-28 pb-10">
                <Link to="/coach" className="inline-flex items-center gap-1 text-zinc-200 hover:text-white mb-6">
                    <ArrowLeft className="w-4 h-4" /> Coach console
                </Link>

                <div className="mb-8">
                    <h1 className="font-display text-5xl md:text-6xl tracking-wider">CHAT ANALYTICS</h1>
                    <p className="text-sm text-zinc-300 mt-2">What non-members ask Max — and how often it converts to a booked assessment.</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Stat label="Conversations (30d)" value={t.conversations_30d} icon={MessageCircle} accent />
                    <Stat label="Last 7 days" value={t.conversations_7d} icon={TrendingUp} />
                    <Stat label="Avg msgs/convo" value={t.avg_messages_per_conversation_30d} icon={Hash} />
                    <Stat label="User messages (30d)" value={t.user_messages_30d} icon={BarChart3} />
                </div>

                {/* Conversion */}
                <div className="border border-brand-navy-700 bg-brand-navy-950/40 rounded-2xl p-5 mb-8">
                    <div className="text-[11px] tracking-wider uppercase text-brand-navy-200 mb-3 inline-flex items-center gap-1">
                        <MousePointer className="w-3 h-3" /> Conversion funnel (30d)
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <FunnelStep label="Conversations started" value={t.conversations_30d} />
                        <FunnelStep label="Clicked the assessment CTA" value={c.unique_convos_with_cta_click_30d} highlight />
                        <FunnelStep
                            label="Click-to-CTA rate"
                            value={`${c.click_rate_pct}%`}
                            sub={`${c.assessment_cta_clicks_30d} total clicks`}
                        />
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    {/* Last 14 days timeline */}
                    <Card title="Last 14 days">
                        {data.timeline_14d.length === 0 ? (
                            <Empty msg="No conversations in the last 14 days." />
                        ) : (
                            <Sparkline data={data.timeline_14d} />
                        )}
                    </Card>

                    {/* Top questions */}
                    <Card title={`Top questions (last 30d)`}>
                        {data.top_questions.length === 0 ? (
                            <Empty msg="No questions yet — Max is fresh." />
                        ) : (
                            <ul className="space-y-1.5 text-sm">
                                {data.top_questions.map((q, i) => (
                                    <li key={i} className="flex items-start gap-3 py-1">
                                        <span className="text-zinc-500 text-[10px] tracking-wider mt-1 shrink-0">×{q.count}</span>
                                        <span className="text-zinc-100">{q.question}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>

                {/* Recent conversations */}
                <Card title="Recent conversations">
                    {data.recent_conversations.length === 0 ? (
                        <Empty msg="No conversations yet." />
                    ) : (
                        <ul className="divide-y divide-zinc-800">
                            {data.recent_conversations.map((c) => (
                                <li key={c.conversation_id} className="py-3 flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm truncate">{c.first_question || "(empty)"}</div>
                                        <div className="text-[10px] text-zinc-400 mt-1 inline-flex items-center gap-3">
                                            <span>{new Date(c.last_message_at).toLocaleString()}</span>
                                            <span>· {c.message_count} msgs</span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`view-convo-${c.conversation_id}`}
                                        onClick={() => setViewing(c.conversation_id)}
                                        className="text-zinc-300 hover:text-white text-xs inline-flex items-center gap-1 shrink-0"
                                    >
                                        <Eye className="w-3.5 h-3.5" /> View
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            {viewing && <TranscriptModal conversationId={viewing} onClose={() => setViewing(null)} />}
        </div>
    );
}

const Stat = ({ label, value, icon: Icon, accent }) => (
    <div className={`border rounded-2xl p-5 bg-zinc-950/50 ${accent ? "border-brand-red/40" : "border-zinc-800"}`}>
        <div className="flex items-center gap-2 text-[11px] tracking-wider uppercase text-zinc-300 mb-2">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
        </div>
        <div className={`font-display text-3xl md:text-4xl tracking-wider ${accent ? "text-brand-red" : "text-white"}`}>{value}</div>
    </div>
);

const FunnelStep = ({ label, value, highlight, sub }) => (
    <div>
        <div className={`font-display text-3xl md:text-4xl tracking-wider ${highlight ? "text-brand-navy-200" : "text-white"}`}>{value}</div>
        <div className="text-[11px] tracking-wider uppercase text-zinc-300 mt-1">{label}</div>
        {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
);

const Card = ({ title, children }) => (
    <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50">
        <div className="px-5 py-4 border-b border-zinc-800">
            <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-200">{title}</h2>
        </div>
        <div className="p-5">{children}</div>
    </div>
);

const Empty = ({ msg }) => <p className="text-sm text-zinc-300">{msg}</p>;

function Sparkline({ data }) {
    const max = Math.max(1, ...data.map(d => d.conversations));
    return (
        <div className="flex items-end gap-1 h-32">
            {data.map((d) => (
                <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center justify-end group relative"
                >
                    <div
                        className="w-full bg-brand-red/30 hover:bg-brand-red rounded-t transition-colors"
                        style={{ height: `${(d.conversations / max) * 100}%` }}
                    />
                    <div className="absolute -top-6 hidden group-hover:block text-[10px] bg-zinc-950 border border-zinc-800 rounded px-2 py-0.5 whitespace-nowrap">
                        {d.date.slice(5)}: {d.conversations}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TranscriptModal({ conversationId, onClose }) {
    const [msgs, setMsgs] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/chat/conversations/${conversationId}/messages`, { withCredentials: true });
                setMsgs(r.data.messages);
            } catch (e) {
                toast.error("Couldn't load transcript", { description: e?.response?.data?.detail });
                onClose();
            }
        })();
    }, [conversationId, onClose]);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-xl max-h-[80vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                    <h3 className="font-display text-xl tracking-wider">TRANSCRIPT</h3>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-zinc-300 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
                    {!msgs ? (
                        <Loader2 className="w-5 h-5 animate-spin text-brand-red mx-auto" />
                    ) : (
                        msgs.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap ${
                                    m.role === "user" ? "bg-brand-red text-white rounded-br-sm" : "bg-zinc-900 text-zinc-100 rounded-bl-sm"
                                }`}>
                                    {m.content}
                                    <div className="text-[9px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
